import { provedorOpencodeNaoTratado, type ProvedorOpencode } from '@remoto/protocolo';
import { dinheiro } from '../configuracoes/regras';
import { rotuloDoCustoDoOpencode } from '../uso/cota';

/**
 * O custo de UM turno em dólar, como a conversa o escreve (F17, R6).
 *
 * Um turno do OpenCode custa frações de centavo — o OC-23 mediu US$ 0,00191
 * num turno de duas voltas no GLM 5.3 Flash. O `dinheiro` da tela de
 * Configurações para em três casas, e escreveria "US$ 0,002": o número certo
 * arredondado até virar outro. Abaixo de um centavo, então, valem DOIS
 * algarismos significativos ("US$ 0,0019"); do centavo para cima, a regra de
 * sempre. Zero é zero — o motor pode relatar um turno de graça, e isso é
 * diferente de não relatar (que nem chega aqui: vira "sem custo relatado").
 */
export function dinheiroDoTurno(usd: number): string {
  if (usd === 0) return dinheiro(0);
  if (usd >= 0.01) return dinheiro(usd);
  return 'US$ ' + usd.toLocaleString('pt-BR', { maximumSignificantDigits: 2 });
}

/** O que a linha do fim do turno diz, peça por peça. */
export interface TextoDoTurno {
  /**
   * "custo real: US$ 0,0019" (OpenRouter) ou "equivalente: US$ 0,0019"
   * (NanoGPT), ou `null` quando o motor não relatou custo nenhum.
   */
  custo: string | null;
  /** O `title` do custo: de onde o número vem e o que ele quer dizer. */
  explicacao: string;
  /** "imagem lida por z-ai/glm-5.3-flash", ou `null`. */
  visao: string | null;
}

/**
 * O que o número do turno é, por provedor (F2, D45), para o `title`.
 *
 * No OpenRouter o `info.cost` é o COBRADO (R6). No NanoGPT é o preço de
 * TABELA, relatado mesmo quando a assinatura cobre a chamada — que sai de
 * graça (medido em 26/10) —, e o dinheiro de verdade é lido da conta, no Uso.
 */
export function explicacaoDoCustoDoTurno(provedor: ProvedorOpencode | null): string {
  if (provedor === null) return 'O custo que o OpenCode relatou para este turno.';
  switch (provedor) {
    case 'nanogpt':
      return 'O preço de tabela deste turno no NanoGPT, como o OpenCode relatou. Coberto pela assinatura, ele não sai do crédito; o dinheiro de verdade está no cartão do OpenCode, em Uso.';
    case 'openrouter':
      return 'O que o OpenRouter cobrou por este turno, como o OpenCode relatou. Não é estimativa.';
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}

/**
 * A linha discreta que fecha um turno do OpenCode.
 *
 * O nome do número é o do PROVEDOR do turno (F2). "Custo real", com essas
 * palavras (R6), só no OpenRouter: lá o `info.cost` é o que ele cobrou, e não
 * uma conta nossa sobre a tabela de preço. No NanoGPT o mesmo campo é o preço
 * de TABELA (D45) — a chamada coberta pela assinatura sai de graça —, e a
 * linha diz "equivalente", como o Claude. Sem provedor sabido, só "custo":
 * chamar um equivalente de "real" seria a tela mentindo sobre dinheiro.
 *
 * Quando alguma chamada do turno veio sem custo, a soma é do que se sabe, e a
 * frase diz isso em vez de deixar o turno parecer mais barato.
 *
 * O modelo da imagem vai pelo id CRU: id feio é melhor que nome inventado.
 */
export function textoDoTurno(item: {
  custoUsd: number | null;
  parcial: boolean;
  imagemLidaPor?: string;
  provedor?: ProvedorOpencode;
}): TextoDoTurno {
  const provedor = item.provedor ?? null;
  const custo =
    item.custoUsd === null
      ? null
      : `${rotuloDoCustoDoOpencode(provedor)}: ${dinheiroDoTurno(item.custoUsd)}` +
        (item.parcial ? ' (parte das chamadas sem custo relatado)' : '');
  return {
    custo,
    explicacao: explicacaoDoCustoDoTurno(provedor),
    visao: item.imagemLidaPor ? `imagem lida por ${item.imagemLidaPor}` : null,
  };
}
